import "./patient-documents.scss";

import { autoinject } from "aurelia-dependency-injection";
import { DialogOpenResult, DialogService, DialogSettings } from "aurelia-dialog";
import { bindable, bindingMode, customElement, observable } from "aurelia-framework";
import { validateTrigger, ValidationController, ValidationControllerFactory } from "aurelia-validation";
import { Router } from "aurelia-router";
import { DialogController } from "aurelia-dialog";

import nameof from "../../../../common/nameof";
import { ClinicalPermissionEnum } from "../../../../enums/clinical-permission-enum";
import { PermissionActionEnum } from "../../../../enums/permission-action-enum";
import { AdministrativePermissionEnum } from "../../../../enums/administrative-permission-enum";
import {
    IPatientDialogOptions,
    IPatientDocument,
    IPatientFileDetails,
    IUploadPatientDocument,
    IUploadPatientDocumentQuery,
    IUploadPatientDocumentResult
} from "../../../../interfaces/i-get-patient";
import { ITypeaheadOptions } from "../../../../interfaces/i-typeahead";
import { IPromptOptions, Prompt } from "../../../../resources/dialogs/prompt/prompt";
import { AssetsService } from "../../../../services/assets-service";
import { DocumentTypeService } from "../../../../services/document-types-service";
import {
    IReferralDocument,
    IUploadReferralDocumentQuery,
    IUploadReferralDocumentResult
} from "../../../../services/i-referral-services";
import { ToastrService } from "../../../../services/toastr-service";
import { EnumMap } from "./../../../../common/utilities/enum-map";
import { PermissionManager } from "./../../../../common/utilities/permission-manager";
import { EnumsService } from "./../../../../services/enums-service";
import { PatientsService } from "./../../../../services/patient-service";
import { ProvidersService } from "./../../../../services/providers-service";
import { ReferralService } from "./../../../../services/referral-service";
import { IFileDetails } from "../../../../interfaces/i-asset";
import { AiService } from "../../../../services/ask-axxess-service";
import { AiUsageStore } from "../../../../common/stores/ai-usage-store";

@autoinject
@customElement("patient-documents")
export class PatientDocuments {
    @bindable({ defaultBindingMode: bindingMode.toView })
    public isPalliative: boolean = false;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public isReferralPage: boolean = false;
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public patientId: string;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public patientVersionId: string = "";
    @bindable({ defaultBindingMode: bindingMode.fromView })
    public getDocuments: (args: { id: string }) => Promise<IPatientDocument[] | IReferralDocument[]>;
    @bindable({ defaultBindingMode: bindingMode.fromView })
    public deleteDocument: (args: { id: string; documentId: string }) => Promise<void>;
    @bindable({ defaultBindingMode: bindingMode.fromView })
    public uploadDocument: (args: {
        id: string;
        newDocument: IUploadReferralDocumentQuery | IUploadPatientDocumentQuery;
    }) => Promise<IUploadReferralDocumentResult | IUploadPatientDocumentResult>;
    private readonly _documentTypeService: DocumentTypeService;
    private readonly _assetsService: AssetsService;
    private readonly _toastrService: ToastrService;
    private readonly _permissionManager: PermissionManager;
    private _dialogService: DialogService;
    private readonly _patientsService: PatientsService;
    private readonly _providersService: ProvidersService;
    private readonly _referralService: ReferralService;
    private readonly _enumsService: EnumsService;
    private _aiUsageStore: AiUsageStore;
    public _aiService: AiService;
    @observable({
        changeHandler: nameof<PatientDocuments>("uploadFileChanged")
    })
    public uploadFile: FileList;
    @observable({
        changeHandler: nameof<PatientDocuments>("moreFilesChanged")
    })
    public moreFiles: FileList;
    public patientDocuments: IPatientDocument[] = [];
    public loadingCount: number = 5;
    public loadingDocuments: boolean = true;
    public validationController: ValidationController;
    public addDocument: boolean = false;
    public searchedTypes: ITypeaheadOptions[];
    public selectedUploadType: ITypeaheadOptions[] = [];
    public fileDetails: IPatientFileDetails[] = [];
    public fileInput: HTMLInputElement;
    public moreFilesInput: HTMLInputElement;
    public uploading: boolean = false;
    public categoryTypeEnumMap: EnumMap = new EnumMap([]);
    public addPatientDocumentsPermission: boolean = false;
    public deletePatientDocumentsPermission: boolean = false;
    public router: Router;
    public patientName: string = "";
    public mrn: string = "";
    public controller: DialogController;
    public isFeatureVisible: boolean = false;
    public hasAskAxxessViewPermission: boolean = false;

    public constructor(
        toastrService: ToastrService,
        documentTypeService: DocumentTypeService,
        assetsService: AssetsService,
        dialogService: DialogService,
        validationControlFactory: ValidationControllerFactory,
        patientsService: PatientsService,
        providersService: ProvidersService,
        enumsService: EnumsService,
        permissionManager: PermissionManager,
        referralService: ReferralService,
        router: Router,
        controller: DialogController,
        aiService: AiService,
        aiUsageStore: AiUsageStore
    ) {
        this.validationController = validationControlFactory.createForCurrentScope();
        this.validationController.validateTrigger = validateTrigger.changeOrBlur;
        this._documentTypeService = documentTypeService;
        this._assetsService = assetsService;
        this._toastrService = toastrService;
        this._dialogService = dialogService;
        this._patientsService = patientsService;
        this._providersService = providersService;
        this._enumsService = enumsService;
        this._referralService = referralService;
        this._permissionManager = permissionManager;
        this.router = router;
        this.controller = controller;
        this._aiService = aiService;
        this._aiUsageStore = aiUsageStore;
    }

    public async attached(options: IPatientDialogOptions) {
        await this.loadSearchedTypes();
        this.addPatientDocumentsPermission = this._permissionManager.checkPermission([
            {
                resource: ClinicalPermissionEnum.ManageDocuments,
                action: PermissionActionEnum.Add
            }
        ]);
        this.deletePatientDocumentsPermission = this._permissionManager.checkPermission([
            {
                resource: ClinicalPermissionEnum.ManageDocuments,
                action: PermissionActionEnum.Delete
            }
        ]);
        this.hasAskAxxessViewPermission = this._permissionManager.checkPermission([
            { resource: AdministrativePermissionEnum.AskAxxess, action: PermissionActionEnum.View }
        ]);
        this.loadFilesList();
    }

    public async loadSearchedTypes() {
        if (!this.patientId) {
            this.searchedTypes = [];
        }
        let categoryTypeEnum = await this._enumsService.getDocumentTypeCategory();
        this.categoryTypeEnumMap = new EnumMap(categoryTypeEnum);
        let category = Number(this.categoryTypeEnumMap.getEnumValue("patient"));
        let searchedTypes = await this._documentTypeService.getDocumentTypesList({
            providerIds: [await this.getProviderId()],
            category
        });
        if (searchedTypes) {
            this.searchedTypes = searchedTypes.map((documentType) => ({
                value: documentType.id,
                name: documentType.type
            }));
        } else {
            this.searchedTypes = [];
        }
    }

    private async getProviderId(): Promise<string> {
        let branchId: string;
        let providerId: string;
        if (this.isReferralPage) {
            if (this.isPalliative) {
                let patientInfo = await this._referralService.getPalliativeReferral(this.patientId);
                branchId = patientInfo.locationId;
                providerId = patientInfo.providerId;
            } else {
                let patientInfo = await this._referralService.getHospiceReferral(this.patientId);
                branchId = patientInfo.locationId;
                providerId = patientInfo.providerId;
            }
        } else {
            let patientInfo = await this._patientsService.getPatientSlim(this.patientId);
            branchId = patientInfo.branchId;
            this.patientName = patientInfo.firstName + " " + patientInfo.lastName;
            this.mrn = patientInfo.medicalRecordNumber;
        }
        if (!!providerId) {
            return providerId;
        } else {
            let providers = await this._providersService.getProvider();
            let patientProvider = providers.find((provider) =>
                provider.branches.some((branch) => branch.id === branchId)
            );
            return patientProvider.id;
        }
    }

    public async loadFilesList() {
        if (this.patientId) {
            this.loadingDocuments = true;
            this.patientDocuments = await this.getDocuments({ id: this.patientId });
            this.loadingDocuments = false;
        }
    }

    public removeFile(index: number) {
        this.fileDetails = this.fileDetails.filter((file, indx) => index !== indx);
    }

    public removeAllFiles() {
        // https://github.com/aurelia/binding/issues/314
        this.fileDetails = [];
        // this.fileInput.value = null;
        // this.moreFiles = null;
    }

    public uploadFileChanged(newValue: File[], oldValue: File[]) {
        let files: File[] = Array.from(newValue);
        this.fileDetails = files.map((file: File) => ({ name: file.name, type: null, file: file }));
    }

    public moreFilesChanged(newValue: File[], oldValue: File[]) {
        let files: File[] = Array.from(newValue);
        this.fileDetails = this.fileDetails.concat(
            files.map((file: File) => ({ name: file.name, type: null, file: file }))
        );
    }

    public async uploadDocuments() {
        let res = await this.validationController.validate();
        if (!res.valid) {
            return;
        }

        if (!this.patientId) {
            await this.showUploadError();
            return;
        }

        try {
            this.uploading = true;
            this.fileDetails.forEach((file, index) => {
                file.attachmentNumber = index;
            });

            let getAssetDetailOfSelectedFilesQuery: IFileDetails[] = this.fileDetails.map((file) => {
                return {
                    fileName: file.name,
                    fileSize: file.file.size,
                    attachmentNumber: file.attachmentNumber
                };
            });

            const { assetDetails } = await this._assetsService.createFilesAsset({
                assets: getAssetDetailOfSelectedFilesQuery
            });

            for (const file of this.fileDetails) {
                let assetDetail = assetDetails.find((asset) => asset.attachmentNumber == file.attachmentNumber);
                if (!assetDetail || !assetDetail.assetId) {
                    throw new Error("Asset Creation Unsuccessful.");
                }
                const currentFile = file.file;
                const currentFileWithUpdatedFileName = new File([currentFile], file.name, { type: currentFile.type });
                await this._assetsService.upload(assetDetail.token, currentFileWithUpdatedFileName);
            }

            let selectedFileDetailsWithAssetId: IPatientFileDetails[] = this.fileDetails.map((file) => {
                let assetDetail = assetDetails.find((asset) => asset.attachmentNumber == file.attachmentNumber);
                let assetId = assetDetail.assetId;
                return { ...file, assetId };
            });

            let patientUploadingDocs: IUploadPatientDocument[] = selectedFileDetailsWithAssetId.map((fileDetail) => {
                let assetId = fileDetail.assetId;
                let documentName = fileDetail.name;
                let uploadType = fileDetail.type.value;
                let key = fileDetail.attachmentNumber;
                return { assetId, documentName, uploadType, key };
            });

            let uploadPatientDocumentQuery: IUploadPatientDocumentQuery = {
                patientVersionId: this.patientVersionId,
                documents: patientUploadingDocs
            };

            await this.uploadDocument({
                id: this.patientId,
                newDocument: uploadPatientDocumentQuery
            });

            this.removeAllFiles();
            this.loadFilesList();
            this._toastrService.success({
                title: `Upload Successful`,
                message: `The files have been successfully uploaded`
            });
        } catch (error) {
            console.error(error);
            await this.showUploadError();
        } finally {
            this.uploading = false;
        }
    }

    public async showUploadError() {
        this._toastrService.error({
            title: `Error`,
            message: `There was a problem while uploading files. Please try again.`
        });
    }

    public async deleteSelectedDocument(documentId: string) {
        try {
            let promptOptions: IPromptOptions = {
                message: "Do you want to delete?",
                okText: "Yes",
                cancelText: "No"
            };
            let dialogOptions: DialogSettings = {
                viewModel: Prompt,
                model: promptOptions
            };
            let dialog = (await this._dialogService.open(dialogOptions)) as DialogOpenResult;
            let closeResult = await dialog.closeResult;

            if (!closeResult.wasCancelled) {
                await this.deleteDocument({ id: this.patientId, documentId });
                this.loadFilesList();
                this._toastrService.success({
                    message: `The document has been successfully deleted.`,
                    title: `Document Deleted`
                });
            }
        } catch (error) {
            this._toastrService.error({
                title: `Error - Document`,
                message: `There was a problem while deleting the document. Please try again.`
            });
        }
    }

    public async isWithinUsageLimitMethod() {
        try {
            const response = await this._aiService.isAxxessFeatureLocked();
            if (response?.status === 200) {
                this._aiUsageStore.isWithinUsageLimit = response.isWithinLimits;
            }
        } catch (err) {
            console.error(err);
        }
    }

    public async summarizationPage(documentId: string, documentName: string, documentDate: string) {
        if (!this._aiUsageStore.isAiPremiumUser) {
            await this.isWithinUsageLimitMethod();
            if (!this._aiUsageStore.isWithinUsageLimit) {
                return;
            }
        }
        this.controller.close(true);
        this.router.navigate(
            `/axxess-intelligence/axxess-intelligence-page?isSummarize=true&documentId=${documentId}&patientName=${this.patientName}&mrn=${this.mrn}&taskName=${documentName}&visitDate=${documentDate}&isExternal=true`
        );
    }
}
